import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Range } from "slate";

import { useToast } from "@smart/bridge-intake-components-dom";
import { EditableInputEditor } from "@smart/itops-sb-design-system-dom";

const LIST_TIP_STORAGE_KEY = "form-builder-list-tip-shown";
const CONSECUTIVE_SPACE_THRESHOLD = 10;

type EditingContextValue = {
  activeEditor: EditableInputEditor | null;
  setActiveEditor: (editor: EditableInputEditor | null) => void;
  registerEditor: (
    editor: EditableInputEditor,
    onContentChange?: (value: string) => void,
  ) => void;
  unregisterEditor: () => void;
  saveContent: (value: string) => void;
  setSelectedText: (selectedText: string) => void;
  selectedText: string;
  currentSelection: Range | null;
  setCurrentSelection: (range: Range | null) => void;
  popupContainerRef: React.RefObject<HTMLElement | null>;
  activeEditorRef: React.RefObject<EditableInputEditor | null>;
  lastEditorRef: React.RefObject<EditableInputEditor | null>;
  lastSelectionRef: React.RefObject<Range | null>;
  onListActivated: () => void;
};

const EditingContext = createContext<EditingContextValue | null>(null);

export const EditingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [activeEditor, setActiveEditor] = useState<EditableInputEditor | null>(
    null,
  );
  const onEditorContentChangeRef = useRef<((value: string) => void) | null>(
    null,
  );
  const popupContainerRef = useRef<HTMLElement | null>(null);
  const lastEditorRef = useRef<EditableInputEditor | null>(null);
  const lastSelectionRef = useRef<Range | null>(null);
  const activeEditorRef = useRef<EditableInputEditor | null>(null);
  const [selectedText, setSelectedText] = useState("");
  const [currentSelection, setCurrentSelection] = useState<Range | null>(null);
  const selectionSyncRef = useRef<string | null>(null);
  activeEditorRef.current = activeEditor;

  const { setToasts } = useToast();
  const consecutiveSpaceCountRef = useRef(0);

  const showTip = useCallback(() => {
    setToasts([
      {
        text: (
          <>
            <strong>Tip:</strong> Press Shift + Return to start a new line.
          </>
        ),
        palette: "default",
      },
    ]);
  }, [setToasts]);

  const onListActivated = useCallback(() => {
    if (localStorage.getItem(LIST_TIP_STORAGE_KEY) === "true") return;
    localStorage.setItem(LIST_TIP_STORAGE_KEY, "true");
    showTip();
  }, [showTip]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeEditorRef.current) return;
      if (e.key === " ") {
        consecutiveSpaceCountRef.current += 1;
        if (consecutiveSpaceCountRef.current > CONSECUTIVE_SPACE_THRESHOLD) {
          showTip();
          consecutiveSpaceCountRef.current = 0;
        }
      } else {
        consecutiveSpaceCountRef.current = 0;
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showTip]);

  // To track the current selection in the editor.
  useEffect(() => {
    if (!activeEditor) {
      setCurrentSelection(null);
      selectionSyncRef.current = null;
      return undefined;
    }
    const sync = () => {
      const sel = activeEditor.selection;
      const key =
        sel == null
          ? null
          : `${sel.anchor.path.join(",")}:${sel.anchor.offset}-${sel.focus.path.join(",")}:${sel.focus.offset}`;
      if (key !== selectionSyncRef.current) {
        selectionSyncRef.current = key;
        setCurrentSelection(sel ?? null);
      }
    };
    sync();
    const id = setInterval(sync, 150);
    return () => clearInterval(id);
  }, [activeEditor]);

  const registerEditor = useCallback(
    (
      editor: EditableInputEditor,
      onContentChange?: (value: string) => void,
    ) => {
      setActiveEditor(editor);
      setCurrentSelection(editor.selection ?? null);
      onEditorContentChangeRef.current = onContentChange ?? null;
    },
    [setCurrentSelection],
  );

  const unregisterEditor = useCallback(() => {
    if (popupContainerRef.current?.contains(document.activeElement ?? null)) {
      return;
    }
    const editor = activeEditorRef.current;
    if (editor !== null) {
      lastEditorRef.current = editor;
      lastSelectionRef.current = editor.selection ?? null;
    }
    setActiveEditor(null);
    setCurrentSelection(null);
    onEditorContentChangeRef.current = null;
  }, []);

  const saveContent = useCallback((value: string) => {
    if (onEditorContentChangeRef.current) {
      onEditorContentChangeRef.current(value);
    }
  }, []);

  const value = useMemo(
    () => ({
      activeEditor,
      setActiveEditor,
      registerEditor,
      unregisterEditor,
      saveContent,
      popupContainerRef,
      setSelectedText,
      selectedText,
      currentSelection,
      setCurrentSelection,
      activeEditorRef,
      lastEditorRef,
      lastSelectionRef,
      onListActivated,
    }),
    [
      activeEditor,
      currentSelection,
      saveContent,
      popupContainerRef,
      registerEditor,
      unregisterEditor,
      setSelectedText,
      selectedText,
      setCurrentSelection,
      onListActivated,
    ],
  );

  return (
    <EditingContext.Provider value={value}>{children}</EditingContext.Provider>
  );
};

export const useEditing = (): EditingContextValue => {
  const value = useContext(EditingContext);
  if (!value) {
    throw new Error("useEditing must be used within EditingProvider");
  }
  return value;
};
